import { QueryStats, useGetDocumentsQuery, useLazyGetDocumentsQuery } from '@api/documents';
import IconButton from '@g17eco/molecules/button/IconButton';
import { ColumnDef } from '@g17eco/molecules/table';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { BasicAlert } from '@g17eco/molecules/alert';
import { DocumentItem, DocumentMediaType, DocumentModalAction } from '@g17eco/types/document';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DocumentsFilters, FilterParams } from './DocumentsFilters';
import { DateRangeType } from '@g17eco/types/common';
import { TIME_RANGE_YEARLY, getDateRange } from '@utils/date';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { InfiniteScrollTable } from '@g17eco/molecules/table/InfiniteScrollTable';

interface Props {
  handleSubmitDocuments: (documentsWithAction: { item: DocumentItem, action: DocumentModalAction }[]) => void;
  disabledDocumentIds?: string[];
  documentIds: string[];
  initiativeId: string;
  isOpen: boolean;
  toggle: () => void;
  modalTitle: string;
  mediaType?: DocumentMediaType;
}

const LIMIT = 10;

const extractDocuments = (
  response: { documents: DocumentItem[]; nextCursor?: string; hasNextPage: boolean } | undefined,
) => {
  if (!response) {
    return {
      items: [],
      hasNextPage: false,
    };
  }
  return {
    items: response.documents,
    nextCursor: response.nextCursor,
    hasNextPage: response.hasNextPage,
  };
};

const getDefaultFilters = (mediaType?: DocumentMediaType) => {
  return {
    searchText: '',
    mediaTypes: mediaType ? [mediaType] : [],
    dateRange: getDateRange(TIME_RANGE_YEARLY),
  };
};

const documentFormatOptions = [
  { label: 'Photos', value: DocumentMediaType.Image },
  { label: 'Videos', value: DocumentMediaType.Video },
  { label: 'Files', value: DocumentMediaType.File },
  // TODO: To support assurance documents
  // { label: 'Assurance', value: DocumentMediaType.Assurance },
];

export const DocumentsModal = ({
  modalTitle,
  documentIds,
  disabledDocumentIds = [],
  initiativeId,
  isOpen,
  toggle,
  handleSubmitDocuments,
  mediaType,
}: Props) => {
  const [filters, setFilters] = useState<FilterParams>(getDefaultFilters(mediaType));
  const queryParams: QueryStats = useMemo(
    () => ({
      initiativeId,
      limit: LIMIT,
      ...filters.dateRange,
      mediaTypes: filters.mediaTypes,
      ownerSubType: filters.ownerSubType,
      searchText: filters.searchText,
    }),
    [filters, initiativeId],
  );
  const [documents, setDocuments] = useState<{ item: DocumentItem, action: DocumentModalAction }[]>([]);
  const { data, isFetching, error } = useGetDocumentsQuery(queryParams);
  const [hasChange, setHasChange] = useState(false);

  const [getDocuments] = useLazyGetDocumentsQuery();

  const { items: visibleDocuments, handleScroll } = useInfiniteScroll({
    initialData: data,
    fetchData: getDocuments,
    extractData: extractDocuments,
    initialParams: queryParams,
  });

  const isDocumentAdded = (docId: string) =>
    documents.some((doc) => doc.action === DocumentModalAction.Add && doc.item._id === docId);
  const isDocumentDisabled = (docId: string) => disabledDocumentIds.some((id) => id === docId);

  useEffect(() => {
    setDocuments((prev) => {
      // ids of documents not fetched yet
      const unseenDocumentIds = documentIds.filter((id) => prev.every((doc) => doc.item._id !== id));
      // add newly fetched documents if they are part of the unseen documents
      const newDocs = visibleDocuments.reduce(
        (acc, document) => {
          if (unseenDocumentIds.some((id) => id === document._id)) {
            acc.push({ item: document, action: DocumentModalAction.Add });
          }
          return acc;
        },
        [] as { item: DocumentItem; action: DocumentModalAction }[],
      );
      if (newDocs.length === 0) {
        return prev;
      }
      return [...newDocs, ...prev];
    });
  }, [documentIds, visibleDocuments]);

  const columns: ColumnDef<DocumentItem>[] = [
    {
      accessorKey: 'title',
      header: 'File name',
      meta: {
        cellProps: {
          style: {
            maxWidth: 200,
          },
          className: 'text-truncate',
        },
      },
      cell: ({ row }) => {
        return (
          <SimpleTooltip text={row.original.title}>
            <span>{row.original.title}</span>
          </SimpleTooltip>
        );
      },
    },
    {
      accessorKey: 'ownerSubType',
      header: 'Type',
    },
    { accessorKey: 'metadata.extension', header: 'Format' },
    {
      id: 'add-button',
      header: '',
      meta: {
        headerProps: {
          style: {
            width: 50,
          },
        },
      },
      cell: ({ row }) => {
        if (isDocumentAdded(row.original._id) || isDocumentDisabled(row.original._id)) {
          return (
            <SimpleTooltip text={isDocumentDisabled(row.original._id) ? 'Already in existing evidences' : ''}>
              <IconButton
                outline
                icon='fal fa-xmark'
                color='error'
                disabled={isDocumentDisabled(row.original._id)}
                className='text-ThemeDangerMedium border border-ThemeDangerMedium icon-button--size-xs btn-xs'
                onClick={() => {
                  if (!hasChange) {
                    setHasChange(true);
                  }
                  const unchangedDocuments = documents.filter((doc) => doc.item._id !== row.original._id);
                  setDocuments([...unchangedDocuments, { item: row.original, action: DocumentModalAction.Remove }]);
                }}
              />
            </SimpleTooltip>
          );
        }
        return (
          <IconButton
            outline
            icon='fal fa-plus'
            color='transparent'
            className='icon-button--size-xs btn-xs'
            onClick={() => {
              if (!hasChange) {
                setHasChange(true);
              }
              setDocuments((prev) => [...prev, { item: row.original, action: DocumentModalAction.Add }]);
            }}
          />
        );
      },
    },
  ];

  const handleChangeFilters = (key: keyof FilterParams, value?: string | string[]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleChangeTimeRange = (dateRange?: DateRangeType) => {
    if (!dateRange) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      dateRange,
    }));
  };

  const handleClose = () => {
    setFilters(getDefaultFilters());
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} backdrop='static'>
      <ModalHeader toggle={handleClose}>{modalTitle}</ModalHeader>
      <ModalBody>
        {error ? <BasicAlert type='danger'>{error.message}</BasicAlert> : null}
        {isFetching ? <BlockingLoader /> : null}
        <DocumentsFilters
          documentFormatOptions={documentFormatOptions}
          filters={filters}
          onChangeFilters={handleChangeFilters}
          onChangeTimeRange={handleChangeTimeRange}
        />
        {visibleDocuments.length === 0 ? (
          <BasicAlert className='mt-2' type='secondary'>
            No available documents
          </BasicAlert>
        ) : (
          <InfiniteScrollTable
            columns={columns}
            data={visibleDocuments}
            handleScroll={handleScroll}
            noData={<BasicAlert type='secondary'>No documents found</BasicAlert>}
          />
        )}
      </ModalBody>
      <ModalFooter className='justify-content-center'>
        <Button color='link-secondary' onClick={handleClose}>
          Close
        </Button>
        <Button color='primary' disabled={!hasChange} onClick={() => handleSubmitDocuments(documents)}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
